import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from 'config';

interface BimModelFile {
  _id: string;
  name: string;
  fileUrl: string;
  order: number;
}

interface BimModelCategory {
  _id: string;
  name: string;
  files: BimModelFile[];
  order: number;
}

const BimModelsPage = () => {
  const [categories, setCategories] = useState<BimModelCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Skeleton data for loading state
  const skeletonCategories = [
    {
      id: 1,
      name: "Загрузка категории...",
      files: Array(3).fill(null)
    },
    {
      id: 2,
      name: "Загрузка категории...",
      files: Array(2).fill(null)
    },
    {
      id: 3,
      name: "Загрузка категории...",
      files: Array(2).fill(null)
    }
  ];

  useEffect(() => {
    const fetchBimModels = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/api/bim-models`);
        
        // Sort categories by order
        const sortedCategories = response.data.sort((a: BimModelCategory, b: BimModelCategory) => a.order - b.order);
        
        // Sort files within each category by order
        sortedCategories.forEach((category: BimModelCategory) => {
          category.files.sort((a: BimModelFile, b: BimModelFile) => a.order - b.order);
        });
        
        setCategories(sortedCategories);
      } catch (err) {
        console.error('Error fetching BIM models:', err);
        // Don't set categories to empty array on error to maintain skeleton layout
      } finally {
        setIsLoading(false);
      }
    };

    fetchBimModels();
  }, []);

  // Render skeleton loading state
  const renderSkeletonState = () => (
    <div className="space-y-8">
      {skeletonCategories.map((category) => (
        <div key={category.id}>
          <div className="h-8 bg-gray-200 rounded w-64 mb-4 animate-pulse"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {category.files.map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-3/4">
                    <div className="h-6 bg-gray-200 rounded w-full animate-pulse mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
                  </div>
                  <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Render actual content
  const renderActualContent = () => (
    <div className="space-y-8">
      {categories.map((category) => (
        <div key={category._id}>
          <h2 className="text-2xl font-semibold mb-4">{category.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {category.files.map((file) => (
              <div key={file._id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{file.name}</h3>
                    <p className="text-sm text-gray-500">Формат: BIM-модель</p>
                    <p className="text-sm text-gray-500">Файл: {file.fileUrl.split('/').pop()}</p>
                  </div>
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <button 
                  onClick={() => window.open(`${API_URL}${file.fileUrl}`, '_blank')}
                  className="mt-4 w-full bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Скачать BIM-модель
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Link 
          to="/support" 
          className="text-primary hover:text-primary-dark transition-colors inline-flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Вернуться к технической поддержке
        </Link>
      </div>

      <h1 className="text-3xl font-bold mb-6">BIM-модели</h1>
      
      {isLoading || categories.length === 0 ? renderSkeletonState() : renderActualContent()}

      <div className="mt-12 bg-gray-50 rounded-lg p-8">
        <h2 className="text-2xl font-semibold mb-4">Запросить индивидуальную BIM-модель</h2>
        <p className="text-gray-600 mb-6">
          Если вы не нашли необходимую BIM-модель или вам требуется индивидуальная модификация, свяжитесь с нашими специалистами.
        </p>
        <Link 
          to="/callback"
          className="bg-primary text-white px-8 py-3 rounded-md hover:bg-primary-dark transition-colors"
        >
          Отправить запрос
        </Link>
      </div>
    </div>
  );
};

export default BimModelsPage; 